import emissionFactorsData from '../data/emission_factors.json';
import sectorProfilesData from '../data/sector_profiles.json';
import demoData from '../data/demo_data.json';

/**
 * Türkçe çeviri fonksiyonları
 */
const translations = {
  // Scope çevirileri
  scopes: {
    '1': 'Doğrudan Emisyonlar',
    '2': 'Dolaylı Enerji Emisyonları', 
    '3': 'Diğer Dolaylı Emisyonlar'
  },
  
  // Kategori çevirileri
  categories: {
    'stationary_combustion': 'Sabit Yakma (Isıtma/Proses)',
    'mobile_combustion': 'Hareketli Yakma (Araçlar)', 
    'purchased_energy': 'Satın Alınan Enerji',
    'purchased_goods': 'Satın Alınan Mallar/Hizmetler',
    'waste_disposal': 'Atık Bertarafı',
    'business_travel': 'İş Seyahatleri',
    'employee_commuting': 'Personel Ulaşımı',
    'transportation': 'Taşımacılık',
    'fugitive_emissions': 'Kaçak Emisyonlar',
    'industrial_processes': 'Endüstriyel Prosesler'
  },

  // Aktivite çevirileri
  activities: {
    'electricity_consumption': '⚡ Elektrik Tüketimi',
    'natural_gas_heating': '🔥 Doğalgaz Isıtması',
    'water_consumption': '💧 Su Tüketimi',
    'food_services': '🍽️ Yemek Hizmetleri',
    'laundry_services': '🧺 Çamaşırhane Hizmetleri',
    'room_occupancy': '🏨 Oda Doluluk Oranı',
    'medical_equipment': '🏥 Tıbbi Ekipman',
    'medical_waste': '🗑️ Tıbbi Atık',
    'laboratory_chemicals': '🧪 Laboratuvar Kimyasalları',
    'administrative_paper': '📄 Ofis Kağıt Kullanımı',
    'office_supplies': '📋 Ofis Malzemeleri',
    'computers_it': '💻 Bilgisayar/IT Ekipmanları',
    'manufacturing_electricity': '⚡ Üretim Elektriği',
    'manufacturing_gas': '🔥 Üretim Doğalgazı',
    'raw_materials': '📦 Ham Maddeler',
    'machinery': '⚙️ Makine İşletimi',
    'diesel': '⛽ Dizel Yakıt',
    'gasoline': '⛽ Benzin Yakıt',
    'vehicle_fleet': '🚗 Araç Filosu',
    'business_travel': '✈️ İş Seyahatleri',
    'employee_commuting': '🚌 Personel Ulaşımı',
    'waste_municipal': '🗑️ Belediye Atığı',
    'paper': '📄 Kağıt Kullanımı',
    'refrigerant': '❄️ Soğutucu Gaz Kaçağı'
  }
};

/**
 * Karbon ayak izi hesaplama utility sınıfı
 * Scope 1, 2, 3 emisyonlarını ve sektör metriklerini hesaplar
 */
class CarbonCalculator {
  constructor(region = 'turkey') {
    this.region = region;
    this.emissionFactors = emissionFactorsData;
    this.sectorProfiles = sectorProfilesData;
    this.demoData = demoData;
    this.customFactors = this.loadCustomFactors();
  }

  /**
   * localStorage'dan custom faktörleri yükler
   * @returns {object} Custom emission factors
   */
  loadCustomFactors() {
    try {
      const stored = localStorage.getItem('customEmissionFactors');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Error loading custom factors:', error);
      return {};
    }
  }

  /**
   * Emisyon faktörünü alır
   * @param {string} activityType - Aktivite tipi
   * @param {string} region - Bölge (varsayılan: turkey)
   * @returns {object} Emisyon faktörü bilgisi
   */
  getEmissionFactor(activityType, region = this.region) {
    // Activity mapping kontrolü - emission_factors.json'dan mapping al
    const mappedActivity = this.emissionFactors.activity_mappings[activityType] || 
                          this.sectorProfiles.activity_emission_mappings[activityType] || 
                          activityType;
    
    // Önce custom faktörleri kontrol et
    if (this.customFactors[region] && this.customFactors[region][mappedActivity]) {
      return {
        ...this.customFactors[region][mappedActivity],
        region: region,
        activity: mappedActivity,
        original_activity: activityType,
        is_custom: true
      };
    }

    const factors = this.emissionFactors.emission_factors;
    
    // Sonra belirtilen bölgeyi kontrol et
    if (factors[region] && factors[region][mappedActivity]) {
      return {
        ...factors[region][mappedActivity],
        region: region,
        activity: mappedActivity,
        original_activity: activityType,
        is_custom: false
      };
    }
    
    // Bulunamazsa global ortalama kullan
    if (factors.global_average && factors.global_average[mappedActivity]) {
      return {
        ...factors.global_average[mappedActivity],
        region: 'global_average',
        activity: mappedActivity,
        original_activity: activityType,
        is_custom: false
      };
    }
    
    throw new Error(`Emission factor not found for activity: ${activityType} (mapped to: ${mappedActivity}) in region: ${region}`);
  }

  /**
   * Tek bir aktivite için emisyon hesaplar
   * @param {string} activityType - Aktivite tipi
   * @param {number} amount - Miktar
   * @param {string} unit - Birim
   * @returns {object} Hesaplama sonucu
   */
  calculateSingleActivity(activityType, amount, unit) {
    try {
      const emissionFactor = this.getEmissionFactor(activityType);
      const expectedUnit = this.emissionFactors.units[emissionFactor.activity] || 
                          this.emissionFactors.units[activityType];
      
      if (!expectedUnit) {
        console.warn(`Unit not defined for activity: ${activityType}, using default calculation`);
      }
      
      // Birim kontrolü (gelecekte unit conversion eklenebilir)
      const emissions = amount * emissionFactor.factor;
      
      // Türkçe çeviriler
      const activityNameTr = translations.activities[activityType] || 
                            translations.activities[emissionFactor.activity] ||
                            activityType.replace(/_/g, ' ');
      const scopeNameTr = translations.scopes[emissionFactor.scope] || `Scope ${emissionFactor.scope}`;
      const categoryNameTr = translations.categories[emissionFactor.category] || emissionFactor.category;
      
      return {
        activity: activityType,
        activity_name_tr: activityNameTr,
        mapped_activity: emissionFactor.activity,
        amount: amount,
        unit: unit,
        expected_unit: expectedUnit,
        emission_factor: emissionFactor.factor,
        emissions_kg_co2e: emissions,
        emissions_ton_co2e: emissions / 1000,
        scope: emissionFactor.scope,
        scope_name_tr: scopeNameTr,
        category: emissionFactor.category,
        category_name_tr: categoryNameTr,
        source: emissionFactor.source,
        region: emissionFactor.region,
        is_custom: emissionFactor.is_custom || false
      };
    } catch (error) {
      console.error(`Error calculating emissions for ${activityType}:`, error);
      return {
        activity: activityType,
        activity_name_tr: translations.activities[activityType] || activityType.replace(/_/g, ' '),
        amount: amount,
        unit: unit,
        error: error.message,
        emissions_kg_co2e: 0,
        emissions_ton_co2e: 0
      };
    }
  }

  /**
   * Tüm aktiviteler için karbon ayak izi hesaplar
   * @param {object} activities - {activityType: {amount, unit}} formatında aktiviteler
   * @param {string} sector - Sektör tipi
   * @param {object} sectorMetrics - Sektör metrikleri (ör: guest_nights)
   * @returns {object} Detaylı hesaplama sonucu
   */
  calculateCarbonFootprint(activities, sector = null, sectorMetrics = {}) {
    const results = {
      calculation_date: new Date().toISOString(),
      region: this.region,
      sector: sector,
      sector_metrics: sectorMetrics,
      activities: [],
      scope_totals: {
        scope_1: { kg_co2e: 0, ton_co2e: 0, activities: [] },
        scope_2: { kg_co2e: 0, ton_co2e: 0, activities: [] },
        scope_3: { kg_co2e: 0, ton_co2e: 0, activities: [] }
      },
      total_emissions: { kg_co2e: 0, ton_co2e: 0 },
      sector_intensity: {},
      emission_factors_used: [],
      calculation_log: []
    };

    // Her aktivite için hesaplama yap
    Object.entries(activities).forEach(([activityType, data]) => {
      if (data.amount && data.amount > 0) {
        const calculation = this.calculateSingleActivity(activityType, data.amount, data.unit);
        results.activities.push(calculation);
        
        if (!calculation.error) {
          // Scope bazında toplama
          const scope = calculation.scope;
          if (results.scope_totals[`scope_${scope}`]) {
            results.scope_totals[`scope_${scope}`].kg_co2e += calculation.emissions_kg_co2e;
            results.scope_totals[`scope_${scope}`].ton_co2e += calculation.emissions_ton_co2e;
            results.scope_totals[`scope_${scope}`].activities.push(activityType);
          }
          
          // Kullanılan faktörleri kaydet
          results.emission_factors_used.push({
            activity: activityType,
            factor: calculation.emission_factor,
            source: calculation.source,
            region: calculation.region
          });
        }
        
        results.calculation_log.push(`${activityType}: ${data.amount} ${data.unit} → ${calculation.emissions_kg_co2e.toFixed(2)} kgCO2e`);
      }
    });

    // Toplam emisyonları hesapla
    results.total_emissions.kg_co2e = 
      results.scope_totals.scope_1.kg_co2e + 
      results.scope_totals.scope_2.kg_co2e + 
      results.scope_totals.scope_3.kg_co2e;
    
    results.total_emissions.ton_co2e = results.total_emissions.kg_co2e / 1000;

    // Sektör yoğunluğu hesapla
    if (sector && sectorMetrics) {
      results.sector_intensity = this.calculateSectorIntensity(
        results.total_emissions.kg_co2e, 
        sector, 
        sectorMetrics
      );
    }

    // Otel sektörü için özel hesaplamalar
    if (sector === 'hotel') {
      results.hotel_analytics = this.calculateHotelAnalytics(results, sectorMetrics);
    }

    return results;
  }

  /**
   * Otel sektörü için özel analitik hesaplamalar
   * @param {object} results - Hesaplama sonuçları
   * @param {object} metrics - Sektör metrikleri
   * @returns {object} Otel analitikleri
   */
  calculateHotelAnalytics(results, metrics) {
    const analytics = {
      room_efficiency: {},
      operational_efficiency: {},
      benchmarks: {},
      recommendations: []
    };

    // Oda başına emisyon hesaplama
    if (metrics.room_count && metrics.room_count > 0) {
      analytics.room_efficiency.emission_per_room = {
        kg_co2e: results.total_emissions.kg_co2e / metrics.room_count,
        ton_co2e: results.total_emissions.ton_co2e / metrics.room_count,
        unit: 'kgCO2e/oda',
        label: 'Oda Başına Emisyon'
      };
    }

    // Doluluk oranına göre verimlilik
    if (metrics.occupancy_rate && metrics.occupancy_rate > 0) {
      analytics.operational_efficiency.occupancy_adjusted = {
        emission_per_occupied_room: results.total_emissions.kg_co2e / (metrics.room_count * metrics.occupancy_rate / 100),
        efficiency_score: Math.max(0, 100 - (metrics.occupancy_rate < 70 ? (70 - metrics.occupancy_rate) * 2 : 0)),
        label: 'Dolu Oda Başına Emisyon'
      };
    }

    // Restoran verimliliği
    if (metrics.restaurant_covers && metrics.restaurant_covers > 0) {
      analytics.operational_efficiency.food_service = {
        emission_per_cover: results.total_emissions.kg_co2e / metrics.restaurant_covers,
        unit: 'kgCO2e/porsiyon',
        label: 'Porsiyon Başına Emisyon'
      };
    }

    // Tesislere göre enerji analizi
    const facilityAreas = {
      spa: metrics.spa_area || 0,
      pool: metrics.pool_area || 0,
      conference: metrics.conference_area || 0
    };

    const totalFacilityArea = Object.values(facilityAreas).reduce((sum, area) => sum + area, 0);
    
    if (totalFacilityArea > 0) {
      analytics.operational_efficiency.facility_impact = {
        total_facility_area: totalFacilityArea,
        emission_per_facility_m2: results.total_emissions.kg_co2e / totalFacilityArea,
        facility_breakdown: facilityAreas,
        unit: 'kgCO2e/m²',
        label: 'Tesis Alanı Başına Emisyon'
      };
    }

    // Benchmark karşılaştırması
    const benchmarkData = this.getBenchmarkData('hotel', 'medium');
    if (benchmarkData && metrics.guest_nights) {
      const actualIntensity = results.total_emissions.kg_co2e / metrics.guest_nights;
      analytics.benchmarks = {
        actual_intensity: actualIntensity,
        benchmark_range: benchmarkData.typical_range?.kg_co2e_per_guest_night,
        performance_rating: this.getPerformanceRating(actualIntensity, benchmarkData),
        improvement_potential: benchmarkData.typical_range?.kg_co2e_per_guest_night?.min 
          ? Math.max(0, actualIntensity - benchmarkData.typical_range.kg_co2e_per_guest_night.min)
          : 0
      };
    }

    // Öneriler oluştur
    analytics.recommendations = this.generateHotelRecommendations(results, metrics, analytics);

    return analytics;
  }

  /**
   * Otel için öneriler oluşturur
   * @param {object} results - Hesaplama sonuçları
   * @param {object} metrics - Sektör metrikleri  
   * @param {object} analytics - Analitik sonuçlar
   * @returns {array} Öneriler listesi
   */
  generateHotelRecommendations(results, metrics, analytics) {
    const recommendations = [];

    // Enerji verimliliği önerileri
    const electricityActivity = results.activities.find(a => a.activity === 'electricity_consumption');
    if (electricityActivity && electricityActivity.emissions_kg_co2e > results.total_emissions.kg_co2e * 0.4) {
      recommendations.push({
        category: 'energy',
        priority: 'high',
        title: 'Elektrik Tüketimi Optimizasyonu',
        description: 'Elektrik tüketimi toplam emisyonların %40\'ından fazlasını oluşturuyor.',
        actions: [
          'LED aydınlatmaya geçiş',
          'Akıllı oda kontrol sistemleri',
          'Enerji verimli HVAC sistemleri',
          'Güneş enerjisi panelleri kurulumu'
        ],
        potential_savings: Math.round(electricityActivity.emissions_kg_co2e * 0.3) + ' kgCO2e'
      });
    }

    // Doluluk oranı önerisi
    if (metrics.occupancy_rate && metrics.occupancy_rate < 60) {
      recommendations.push({
        category: 'operations',
        priority: 'medium',
        title: 'Doluluk Oranı Optimizasyonu',
        description: `Mevcut doluluk oranı %${metrics.occupancy_rate.toFixed(1)} - düşük doluluk enerji verimliliğini azaltır.`,
        actions: [
          'Dinamik fiyatlandırma stratejileri',
          'Pazarlama kampanyaları',
          'Paket programlar',
          'Mevsimsel operasyon ayarlamaları'
        ]
      });
    }

    // Atık yönetimi önerisi
    const wasteActivities = results.activities.filter(a => 
      a.activity.includes('waste') || a.activity.includes('food_waste')
    );
    if (wasteActivities.length > 0) {
      const totalWasteEmissions = wasteActivities.reduce((sum, a) => sum + a.emissions_kg_co2e, 0);
      recommendations.push({
        category: 'waste',
        priority: 'medium', 
        title: 'Atık Yönetimi İyileştirmesi',
        description: 'Atık kaynaklı emisyonları azaltmak için geri dönüşüm ve azaltım programları',
        actions: [
          'Yemek atığı azaltım programı',
          'Kapsamlı geri dönüşüm sistemi',
          'Kompost üretimi',
          'Tek kullanımlık ürün azaltımı'
        ],
        potential_savings: Math.round(totalWasteEmissions * 0.5) + ' kgCO2e'
      });
    }

    // Su tüketimi önerisi
    const waterActivity = results.activities.find(a => a.activity === 'water_consumption');
    if (waterActivity && metrics.guest_nights) {
      const waterPerGuest = waterActivity.amount / metrics.guest_nights;
      if (waterPerGuest > 300) { // m³ su per guest-night
        recommendations.push({
          category: 'water',
          priority: 'medium',
          title: 'Su Tasarrufu Programı',
          description: `Misafir başına su tüketimi ${waterPerGuest.toFixed(1)} m³ - sektör ortalamasının üstünde`,
          actions: [
            'Su tasarruflu armatürler',
            'Havuz ve spa sistemleri optimizasyonu',
            'Yağmur suyu toplama',
            'Misafir bilinçlendirme programları'
          ]
        });
      }
    }

    return recommendations;
  }

  /**
   * Performans derecelendirmesi yapar
   * @param {number} actualValue - Gerçek değer
   * @param {object} benchmark - Benchmark verileri
   * @returns {string} Performans derecesi
   */
  getPerformanceRating(actualValue, benchmark) {
    if (!benchmark.typical_range?.kg_co2e_per_guest_night) return 'unknown';
    
    const { min, max, median } = benchmark.typical_range.kg_co2e_per_guest_night;
    
    if (actualValue <= min) return 'excellent';
    if (actualValue <= median) return 'good';
    if (actualValue <= max) return 'average';
    return 'needs_improvement';
  }

  /**
   * Sektör yoğunluğu hesaplar
   * @param {number} totalEmissions - Toplam emisyon (kgCO2e)
   * @param {string} sector - Sektör tipi
   * @param {object} metrics - Sektör metrikleri
   * @returns {object} Sektör yoğunluğu
   */
  calculateSectorIntensity(totalEmissions, sector, metrics) {
    const sectorProfile = this.sectorProfiles.sectors[sector];
    if (!sectorProfile) {
      return { error: `Sector profile not found: ${sector}` };
    }

    const intensity = {};
    const metricField = sectorProfile.required_fields[0]; // İlk gerekli alan metrik olarak kullanılır
    
    if (metrics[metricField] && metrics[metricField] > 0) {
      const intensityValue = totalEmissions / metrics[metricField];
      intensity[sectorProfile.output_metric.unit] = intensityValue;
      intensity.label = sectorProfile.output_metric.label;
      intensity.metric_value = metrics[metricField];
      intensity.metric_unit = sectorProfile.field_definitions[metricField].unit;
    }

    return intensity;
  }

  /**
   * Sektör için gerekli ve opsiyonel alanları döndürür
   * @param {string} sector - Sektör tipi
   * @returns {object} Alan tanımları
   */
  getSectorFields(sector) {
    const sectorProfile = this.sectorProfiles.sectors[sector];
    if (!sectorProfile) {
      throw new Error(`Sector not found: ${sector}`);
    }

    return {
      sector_info: {
        name: sectorProfile.name,
        output_metric: sectorProfile.output_metric
      },
      required_fields: sectorProfile.activity_categories.required.map(activity => ({
        activity,
        ...sectorProfile.field_definitions[activity]
      })),
      optional_fields: sectorProfile.activity_categories.optional.map(activity => ({
        activity,
        ...sectorProfile.field_definitions[activity]
      })),
      sector_metrics: sectorProfile.required_fields.map(field => ({
        field,
        ...sectorProfile.field_definitions[field]
      }))
    };
  }

  /**
   * Mevcut sektörleri listeler
   * @returns {array} Sektör listesi
   */
  getAvailableSectors() {
    return Object.entries(this.sectorProfiles.sectors).map(([key, sector]) => ({
      key,
      name: sector.name,
      name_en: sector.name_en,
      output_metric: sector.output_metric
    }));
  }

  /**
   * Hesaplama sonuçlarını CSV formatına dönüştürür
   * @param {object} results - Hesaplama sonuçları
   * @returns {string} CSV içeriği
   */
  exportToCSV(results) {
    const csvRows = [];
    
    // Header
    csvRows.push('Activity,Amount,Unit,Emissions (kgCO2e),Emissions (tCO2e),Scope,Category,Source');
    
    // Activities
    results.activities.forEach(activity => {
      if (!activity.error) {
        csvRows.push([
          activity.activity,
          activity.amount,
          activity.unit,
          activity.emissions_kg_co2e.toFixed(2),
          activity.emissions_ton_co2e.toFixed(3),
          activity.scope,
          activity.category,
          activity.source
        ].join(','));
      }
    });
    
    // Totals
    csvRows.push('');
    csvRows.push('TOTALS');
    csvRows.push(`Scope 1,,,${results.scope_totals.scope_1.kg_co2e.toFixed(2)},${results.scope_totals.scope_1.ton_co2e.toFixed(3)},,`);
    csvRows.push(`Scope 2,,,${results.scope_totals.scope_2.kg_co2e.toFixed(2)},${results.scope_totals.scope_2.ton_co2e.toFixed(3)},,`);
    csvRows.push(`Scope 3,,,${results.scope_totals.scope_3.kg_co2e.toFixed(2)},${results.scope_totals.scope_3.ton_co2e.toFixed(3)},,`);
    csvRows.push(`TOTAL,,,${results.total_emissions.kg_co2e.toFixed(2)},${results.total_emissions.ton_co2e.toFixed(3)},,`);
    
    if (results.sector_intensity && results.sector_intensity.label) {
      csvRows.push('');
      csvRows.push('SECTOR INTENSITY');
      csvRows.push(`${results.sector_intensity.label},${results.sector_intensity.metric_value},${results.sector_intensity.metric_unit},${Object.values(results.sector_intensity)[0].toFixed(2)},,`);
    }
    
    return csvRows.join('\n');
  }

  /**
   * Hesaplama sonuçlarını validasyon yapar
   * @param {object} results - Hesaplama sonuçları
   * @returns {object} Validasyon sonucu
   */
  validateResults(results) {
    const validation = {
      is_valid: true,
      warnings: [],
      errors: []
    };

    // Toplam kontrolü
    const calculatedTotal = 
      results.scope_totals.scope_1.kg_co2e + 
      results.scope_totals.scope_2.kg_co2e + 
      results.scope_totals.scope_3.kg_co2e;
    
    if (Math.abs(calculatedTotal - results.total_emissions.kg_co2e) > 0.01) {
      validation.errors.push('Total emissions calculation mismatch');
      validation.is_valid = false;
    }

    // Emisyon faktörü kontrolü
    const missingFactors = results.activities.filter(activity => activity.error);
    if (missingFactors.length > 0) {
      validation.warnings.push(`Missing emission factors for: ${missingFactors.map(a => a.activity).join(', ')}`);
    }

    // Sıfır emisyon kontrolü
    if (results.total_emissions.kg_co2e === 0) {
      validation.warnings.push('Total emissions is zero - please check input data');
    }

    return validation;
  }

  /**
   * Demo senaryolarını döndürür
   * @param {string} size - 'small', 'medium', 'luxury'
   * @returns {object} Demo scenario data
   */
  getDemoScenario(size = 'small') {
    const scenarioKey = `hotel_${size}`;
    return this.demoData.demo_scenarios[scenarioKey] || null;
  }

  /**
   * Tüm demo senaryolarını listeler
   * @returns {array} Demo scenarios list
   */
  getAllDemoScenarios() {
    return Object.entries(this.demoData.demo_scenarios).map(([key, scenario]) => ({
      key,
      ...scenario
    }));
  }

  /**
   * Benchmark verilerini döndürür
   * @param {string} sector - Sektör
   * @param {string} size - Hotel size
   * @returns {object} Benchmark data
   */
  getBenchmarkData(sector = 'hotel', size = 'medium') {
    if (sector === 'hotel') {
      const sizeKey = `${size}_hotel`;
      return this.demoData.benchmark_data.hotel_sector_benchmarks[sizeKey] || null;
    }
    return null;
  }

  /**
   * Hesaplama sonuçlarına göre iyileştirme önerileri
   * @param {object} results - Calculation results
   * @returns {array} Improvement suggestions
   */
  getImprovementSuggestions(results) {
    const suggestions = [];
    const totalEmissions = results.total_emissions.kg_co2e;
    const guestNights = results.sector_metrics?.guest_nights;
    
    if (!guestNights || guestNights === 0) {
      return suggestions;
    }

    const emissionPerGuest = totalEmissions / guestNights;
    
    // Elektrik yoğunluğu kontrolü
    const electricityActivity = results.activities.find(a => a.activity === 'electricity_consumption');
    if (electricityActivity && !electricityActivity.error) {
      const electricityEmissionPerGuest = electricityActivity.emissions_kg_co2e / guestNights;
      if (electricityEmissionPerGuest > 8) {
        suggestions.push({
          category: 'Elektrik Tüketimi',
          priority: 'Yüksek',
          current_value: electricityEmissionPerGuest.toFixed(2),
          suggestions: this.demoData.benchmark_data.improvement_suggestions.high_electricity.suggestions
        });
      }
    }

    // Isıtma yoğunluğu kontrolü
    const heatingActivity = results.activities.find(a => a.activity === 'natural_gas_heating');
    if (heatingActivity && !heatingActivity.error) {
      const heatingEmissionPerGuest = heatingActivity.emissions_kg_co2e / guestNights;
      if (heatingEmissionPerGuest > 3) {
        suggestions.push({
          category: 'Isıtma Sistemi',
          priority: 'Orta',
          current_value: heatingEmissionPerGuest.toFixed(2),
          suggestions: this.demoData.benchmark_data.improvement_suggestions.high_heating.suggestions
        });
      }
    }

    // Atık yoğunluğu kontrolü
    const wasteActivities = results.activities.filter(a => 
      (a.activity.includes('waste') || a.activity === 'food_waste') && !a.error
    );
    const totalWasteEmissions = wasteActivities.reduce((sum, a) => sum + a.emissions_kg_co2e, 0);
    const wasteEmissionPerGuest = totalWasteEmissions / guestNights;
    
    if (wasteEmissionPerGuest > 1) {
      suggestions.push({
        category: 'Atık Yönetimi',
        priority: 'Orta',
        current_value: wasteEmissionPerGuest.toFixed(2),
        suggestions: this.demoData.benchmark_data.improvement_suggestions.high_waste.suggestions
      });
    }

    return suggestions;
  }

  /**
   * Test senaryolarını çalıştırır
   * @returns {array} Test results
   */
  runTestCases() {
    const testResults = [];
    
    this.demoData.test_cases.forEach((testCase, index) => {
      try {
        const result = this.calculateCarbonFootprint(
          testCase.input.activities,
          testCase.input.sector,
          testCase.input.sector_metrics
        );
        
        const validation = this.validateResults(result);
        
        testResults.push({
          test_name: testCase.name,
          status: 'passed',
          result: result,
          validation: validation,
          expected: testCase.expected_ranges || testCase.expected_validation
        });
      } catch (error) {
        testResults.push({
          test_name: testCase.name,
          status: 'failed',
          error: error.message,
          expected: testCase.expected_ranges || testCase.expected_validation
        });
      }
    });
    
    return testResults;
  }
}

// Default export
export default CarbonCalculator;

// Named exports
export { CarbonCalculator };

// Utility functions
export const formatEmissions = (emissions, unit = 'kg') => {
  if (unit === 'ton' || unit === 't') {
    return `${(emissions / 1000).toFixed(2)} tCO2e`;
  }
  return `${emissions.toFixed(2)} kgCO2e`;
};

export const getScopeColor = (scope) => {
  const colors = {
    '1': '#FF6B6B', // Kırmızı - Doğrudan emisyonlar
    '2': '#4ECDC4', // Teal - Satın alınan enerji
    '3': '#45B7D1'  // Mavi - Diğer dolaylı emisyonlar
  };
  return colors[scope] || '#95A5A6';
};

export const getCategoryIcon = (category) => {
  const icons = {
    'stationary_combustion': '🔥',
    'mobile_combustion': '🚗',
    'fugitive_emissions': '💨',
    'purchased_energy': '⚡',
    'business_travel': '✈️',
    'employee_commuting': '🚌',
    'waste_disposal': '🗑️',
    'purchased_goods': '📦'
  };
  return icons[category] || '📊';
};
